import * as THREE from 'three'
import { dyno } from '@sparkjsdev/spark'
import { defaultSettings, effectFamilies } from '../settings/appSettings.js'

export function createEffectUniforms(effectParams) {
  return {
    animateT: dyno.dynoFloat(0),
    effectEnabled: dyno.dynoInt(effectParams.enabled ? 1 : 0),
    familyType: dyno.dynoInt(1),
    effectType: dyno.dynoInt(1),
    effectIntensity: dyno.dynoFloat(effectParams.intensity),
    revealCenter: dyno.dynoVec3(new THREE.Vector3()),
    revealRadius: dyno.dynoFloat(1)
  }
}

export function syncEffectUniforms({ effectParams, effectRuntime, uniforms }) {
  const families = Object.keys(effectFamilies)
  if (!effectFamilies[effectParams.family]) {
    effectParams.family = defaultSettings.effect.family
  }
  if (!effectFamilies[effectParams.family].includes(effectParams.effect)) {
    effectParams.effect = effectFamilies[effectParams.family][0]
  }
  uniforms.effectEnabled.value = effectParams.enabled ? 1 : 0
  uniforms.familyType.value = families.indexOf(effectParams.family) + 1
  uniforms.effectType.value = effectFamilies[effectParams.family].indexOf(effectParams.effect) + 1
  uniforms.effectIntensity.value = effectRuntime.intensity
}

export function createEffectsModifier(effectUniforms) {
  const {
    animateT,
    effectEnabled,
    familyType,
    effectType,
    effectIntensity,
    revealCenter,
    revealRadius
  } = effectUniforms

  return dyno.dynoBlock(
    { gsplat: dyno.Gsplat },
    { gsplat: dyno.Gsplat },
    ({ gsplat }) => {
      const shader = new dyno.Dyno({
        inTypes: {
          gsplat: dyno.Gsplat,
          time: 'float',
          enabled: 'int',
          family: 'int',
          effect: 'int',
          intensity: 'float',
          revealCenter: 'vec3',
          revealRadius: 'float'
        },
        outTypes: { gsplat: dyno.Gsplat },
        globals: () => [
          dyno.unindent(`
            vec3 hash3(vec3 p) {
              p = fract(p * 0.3183099 + 0.1);
              p *= 17.0;
              return fract(vec3(p.x * p.y * p.z, p.x + p.y * p.z, p.x * p.y + p.z));
            }

            float hash11(float p) {
              p = fract(p * 0.1031);
              p += dot(p, p + 33.33);
              return fract(p * p);
            }

            float hash13(vec3 p) {
              p = fract(p * 0.1031);
              p += dot(p, p.yzx + 33.33);
              return fract((p.x + p.y) * p.z);
            }

            vec3 noise3(vec3 p) {
              vec3 i = floor(p);
              vec3 f = fract(p);
              f = f * f * (3.0 - 2.0 * f);

              vec3 n000 = hash3(i + vec3(0.0, 0.0, 0.0));
              vec3 n100 = hash3(i + vec3(1.0, 0.0, 0.0));
              vec3 n010 = hash3(i + vec3(0.0, 1.0, 0.0));
              vec3 n110 = hash3(i + vec3(1.0, 1.0, 0.0));
              vec3 n001 = hash3(i + vec3(0.0, 0.0, 1.0));
              vec3 n101 = hash3(i + vec3(1.0, 0.0, 1.0));
              vec3 n011 = hash3(i + vec3(0.0, 1.0, 1.0));
              vec3 n111 = hash3(i + vec3(1.0, 1.0, 1.0));

              vec3 x0 = mix(n000, n100, f.x);
              vec3 x1 = mix(n010, n110, f.x);
              vec3 x2 = mix(n001, n101, f.x);
              vec3 x3 = mix(n011, n111, f.x);
              vec3 y0 = mix(x0, x1, f.y);
              vec3 y1 = mix(x2, x3, f.y);
              return mix(y0, y1, f.z);
            }

            mat2 rot2(float a) {
              float s = sin(a);
              float c = cos(a);
              return mat2(c, -s, s, c);
            }

            float ease3(float x) {
              x = clamp(x, 0.0, 1.0);
              return x * x * (3.0 - 2.0 * x);
            }

            vec4 twisterReveal(vec3 pos, vec3 scale, float t) {
              vec3 h = hash3(pos);
              float s = smoothstep(0.0, 8.0, t * t * 0.1 - length(pos.xz) * 2.0 + 2.0);
              if (length(scale) < 0.05) pos.y = mix(-10.0, pos.y, pow(s, 2.0 * h.x));
              pos.xz = mix(pos.xz * 0.5, pos.xz, pow(s, 2.0 * h.x));
              float rotationTime = t * (1.0 - s) * 0.2;
              pos.xz *= rot2(rotationTime + pos.y * 20.0 * (1.0 - s) * exp(-length(pos.xz)));
              return vec4(pos, s * s * s * s);
            }

            vec4 rainReveal(vec3 pos, vec3 scale, float t) {
              vec3 h = hash3(pos);
              float s = pow(smoothstep(0.0, 5.0, t * t * 0.1 - length(pos.xz) * 2.0 + 1.0), 0.5 + h.x);
              float y = pos.y;
              pos.y = min(-10.0 + s * 15.0, pos.y);
              pos.xz = mix(pos.xz * 0.3, pos.xz, s);
              return vec4(pos, smoothstep(-10.0, y, pos.y));
            }

            vec3 headMovement(vec3 pos, float t) {
              pos.xy *= rot2(smoothstep(-1.0, -2.0, pos.y) * 0.2 * sin(t * 2.0));
              return pos;
            }

            vec3 breathAnimation(vec3 pos, float t) {
              float b = sin(t * 1.5);
              pos.yz *= rot2(smoothstep(-1.0, -3.0, pos.y) * 0.15 * -b);
              pos.z += 0.3;
              pos.y += 1.2;
              pos *= 1.0 + exp(-3.0 * length(pos)) * b;
              pos.z -= 0.3;
              pos.y -= 1.2;
              return pos;
            }

            vec4 fractalElectronic(vec3 pos, float t, float intensity) {
              float m = 100.0;
              vec3 p = pos * 0.1;
              p.y += 0.5;
              for (int i = 0; i < 8; i++) {
                p = abs(p) / clamp(abs(p.x * p.y), 0.3, 3.0) - 1.0;
                p.xy *= rot2(radians(90.0));
                if (i > 1) m = min(m, length(p.xy) + step(0.3, fract(p.z * 0.5 + t * 0.5 + float(i) * 0.2)));
              }
              m = step(m, 0.5) * 1.3 * intensity;
              return vec4(-pos.y * 0.3, 0.5, 0.7, 0.3) * intensity + m;
            }

            vec4 fractalMeditation(vec3 center, vec3 scales, vec4 rgba, float t, float intensity) {
              vec3 pos = center;
              float splatSize = length(scales);
              vec3 p = pos * 0.65;
              pos.y += 2.0;
              float c = 0.0;
              float l;
              float l2 = length(p);
              float m = 100.0;

              for (int i = 0; i < 10; i++) {
                p.xyz = abs(p.xyz) / dot(p.xyz, p.xyz) - 0.8;
                l = length(p.xyz);
                c += exp(-abs(l - l2) * (1.0 + sin(t * 1.5 + pos.y)));
                l2 = length(p.xyz);
                m = min(m, length(p.xyz));
              }

              c = smoothstep(0.3, 0.5, m + sin(t * 1.5 + pos.y * 0.5)) + c * 0.1;
              return vec4(vec3(length(rgba.rgb)) * vec3(c, c * c, c * c * c) * intensity,
                rgba.a * exp(-20.0 * splatSize) * m * intensity);
            }

            vec4 waveColor(vec3 p, float t) {
              float m = exp(-2.0 * length(sin(p * 5.0 + t * 3.0))) * 5.0;
              return vec4(m) + 0.3;
            }

            vec4 shaderDisintegrate(vec3 pos, float t, float intensity) {
              vec3 p = pos + (hash3(pos) * 2.0 - 1.0) * intensity;
              float tt = smoothstep(-1.0, 0.5, -sin(t - pos.y * 0.5));
              p.xz *= rot2(tt * 2.0 + p.y * 2.0 * tt);
              return vec4(mix(p, pos, tt), tt);
            }

            vec4 flareEffect(vec3 pos, float t) {
              vec3 p = vec3(0.0, -1.5, 0.0);
              float tt = smoothstep(-1.0, 0.5, sin(t + hash3(pos).x));
              tt *= tt;
              p.x += sin(t * 2.0) * tt;
              p.z += sin(t * 2.0) * tt;
              p.y += sin(t) * tt;
              return vec4(mix(pos, p, tt), tt);
            }

            vec4 dissolveEffect(vec3 pos, float t) {
              vec3 h = hash3(pos);
              float startTime = h.x * 8.0;
              float effectTime = min(t, 12.0);
              float enabled = step(startTime, effectTime);
              float flicker = sin(t * 10.0 + h.y * 6.2831853) * 0.5 + 0.5;
              vec3 dir = normalize(vec3(1.0, (h.y - 0.5) * 0.9, (h.z - 0.5) * 0.9));
              float speed = hash13(pos + vec3(12.0, 78.0, 45.0));
              float amount = effectTime * 0.12 * speed * enabled;
              return vec4(dir * amount, mix(1.0, 1.0 - clamp(amount / 1.8, 0.0, 1.0), enabled) + flicker * enabled * 0.15);
            }

            vec4 transitionSphere(vec3 pos, vec3 scales, float t, float intensity) {
              float phase = min(t * 0.18, 1.0);
              float f = abs(mix(-1.0, 1.0, phase));
              vec3 target = normalize(pos - vec3(0.0, 0.2, 0.0)) * (0.6 + intensity * 0.8);
              return vec4(mix(target, pos, pow(f, 3.0)), f);
            }

            vec4 transitionExplosion(vec3 pos, float t, float intensity) {
              float phase = min(t * 0.18, 1.0);
              float blast = ease3(min(phase * 2.0, 1.0));
              float reform = ease3(max((phase - 0.5) * 2.0, 0.0));
              vec3 dir = normalize(pos + hash3(pos) * 0.5);
              vec3 exploded = pos + dir * blast * intensity * 2.5;
              exploded.y += blast * (1.0 - blast) * 2.0;
              return vec4(mix(exploded, pos, reform), 1.0 - blast * (1.0 - reform));
            }

            vec4 transitionFlow(vec3 pos, float t, float intensity) {
              float phase = min(t * 0.16, 1.0);
              float f = abs(mix(-1.0, 1.0, phase));
              vec3 target = phase < 0.5 ? vec3(-1.0, 0.0, 0.0) : vec3(1.0, 0.3, 0.0);
              float id = hash13(pos);
              vec3 flowed = mix(target, pos, pow(f, 0.7 + id * 1.8));
              flowed += length(sin(flowed * 2.5)) * intensity * (1.0 - f) * vec3(0.0, 0.8, 0.0);
              return vec4(flowed, 0.25 + f);
            }

            vec4 transitionMorph(vec3 pos, int index, float t, float radius) {
              float phase = min(t * 0.14, 1.0);
              float scatterOut = ease3(min(phase * 2.0, 1.0));
              float gatherIn = ease3(max((phase - 0.5) * 2.0, 0.0));
              float h = hash11(float(index));
              float theta = 6.2831853 * h;
              vec3 randomPos = vec3(radius * cos(theta), (hash11(float(index) + 1.0) - 0.5) * 0.6, radius * sin(theta));
              vec3 mid = mix(pos, randomPos, 0.85);
              return vec4(mix(mix(pos, mid, scatterOut), pos, gatherIn), max(1.0 - scatterOut * 0.5, gatherIn));
            }
          `)
        ],
        statements: ({ inputs, outputs }) => dyno.unindentLines(`
          ${outputs.gsplat} = ${inputs.gsplat};

          vec3 localPos = ${inputs.gsplat}.center;
          vec3 scales = ${inputs.gsplat}.scales;
          vec4 color = ${inputs.gsplat}.rgba;
          float t = ${inputs.time};
          float intensity = ${inputs.intensity};

          if (${inputs.enabled} == 0) {
            ${outputs.gsplat} = ${inputs.gsplat};
          } else if (${inputs.family} == 1) {
            vec3 origin = ${inputs.revealCenter};
            float radius = max(${inputs.revealRadius}, 0.0001);
            vec3 revealPos = (localPos - origin) / radius;
            float progress = min(t * 0.06, 1.0);
            float revealProgress = min(progress / 0.78, 1.0);
            float reveal = revealProgress * 2.8 - 0.35;
            float l = length(revealPos.xz);
            float radialVisible = 1.0 - smoothstep(reveal - 0.08, reveal + 0.18, l);
            float border = 1.0 - smoothstep(0.0, 0.16, abs(l - reveal));

            if (${inputs.effect} == 1) {
              vec3 warped = revealPos * (1.0 - 0.12 * border * intensity);
              vec3 noiseOffset = (noise3(revealPos * 2.0 + t * 0.5) - 0.5) * radius * 0.08 * border * intensity;
              ${outputs.gsplat}.center = origin + warped * radius + noiseOffset;
              ${outputs.gsplat}.scales = mix(scales * 0.1, scales, radialVisible);
              ${outputs.gsplat}.rgba = color;
              ${outputs.gsplat}.rgba.a = color.a * radialVisible;
              ${outputs.gsplat}.rgba.rgb += vec3(1.0, 0.65, 0.2) * border * intensity;
            } else if (${inputs.effect} == 2) {
              vec3 spreadPos = revealPos;
              spreadPos.xz *= mix(0.35, 1.0, radialVisible);
              ${outputs.gsplat}.center = origin + spreadPos * radius;
              ${outputs.gsplat}.scales = mix(scales * 0.05, scales, radialVisible);
              ${outputs.gsplat}.rgba = mix(vec4(vec3(0.3), color.a), color, radialVisible);
              ${outputs.gsplat}.rgba.a = color.a * radialVisible;
            } else if (${inputs.effect} == 3) {
              float unrollSweep = revealProgress * 2.8 - 0.35;
              float verticalTarget = revealPos.y + 1.05;
              float verticalVisible = smoothstep(verticalTarget - 0.16, verticalTarget + 0.16, unrollSweep);
              revealPos.xz *= rot2((revealPos.y * 8.0 - 3.0) * (1.0 - verticalVisible));
              ${outputs.gsplat}.center = origin + revealPos * radius;
              ${outputs.gsplat}.scales = mix(scales * 0.08, scales, verticalVisible);
              ${outputs.gsplat}.rgba = color;
              ${outputs.gsplat}.rgba.a = color.a * verticalVisible;
            } else if (${inputs.effect} == 4) {
              vec4 effectResult = twisterReveal(revealPos, scales / radius, revealProgress * 10.0);
              ${outputs.gsplat}.center = origin + effectResult.xyz * radius;
              ${outputs.gsplat}.scales = mix(scales * 0.08, scales, pow(effectResult.w, 3.0));
              ${outputs.gsplat}.rgba.a = color.a * effectResult.w;
              float spin = -t * 0.3 * (1.0 - effectResult.w);
              vec4 spinQ = vec4(0.0, sin(spin * 0.5), 0.0, cos(spin * 0.5));
              ${outputs.gsplat}.quaternion = quatQuat(spinQ, ${inputs.gsplat}.quaternion);
            } else {
              float rainT = revealProgress * 1.35;
              float topToBottomOrder = clamp((revealPos.y + 1.05) * 0.5, 0.0, 1.0);
              float dropDelay = hash13(revealPos) * 0.08;
              float rainVisible = smoothstep(topToBottomOrder - 0.06 + dropDelay, topToBottomOrder + 0.12 + dropDelay, rainT);
              vec3 rainStart = revealPos;
              rainStart.y = min(revealPos.y - 1.2, -1.4) - hash3(revealPos).y * 0.5;
              ${outputs.gsplat}.center = origin + mix(rainStart, revealPos, rainVisible) * radius;
              ${outputs.gsplat}.scales = mix(scales * 0.08, scales, rainVisible);
              ${outputs.gsplat}.rgba = color;
              ${outputs.gsplat}.rgba.a = color.a * rainVisible;
            }
          } else if (${inputs.family} == 2) {
            if (${inputs.effect} == 1) {
              ${outputs.gsplat}.center = headMovement(localPos, t);
              vec4 fx = fractalElectronic(localPos, t, intensity);
              ${outputs.gsplat}.rgba = mix(color, color * fx, intensity);
            } else if (${inputs.effect} == 2) {
              vec4 fx = fractalMeditation(localPos, scales, color, t, intensity);
              ${outputs.gsplat}.rgba = mix(color, fx, intensity);
              ${outputs.gsplat}.center = breathAnimation(localPos, t);
            } else if (${inputs.effect} == 3) {
              vec4 fx = waveColor(localPos, t);
              ${outputs.gsplat}.rgba = mix(color, color * fx, intensity);
              vec3 pos = localPos;
              pos.y += 1.0;
              pos *= 1.0 + fx.x * 0.05 * intensity;
              pos.y -= 1.0;
              ${outputs.gsplat}.center = pos;
            } else if (${inputs.effect} == 4) {
              vec4 fx = shaderDisintegrate(localPos, t, intensity);
              ${outputs.gsplat}.center = fx.xyz;
              ${outputs.gsplat}.scales = mix(vec3(0.01), scales, fx.w);
            } else {
              vec4 fx = flareEffect(localPos, t);
              ${outputs.gsplat}.center = fx.xyz;
              ${outputs.gsplat}.rgba.rgb = mix(color.rgb, vec3(1.0), abs(fx.w) * intensity);
              ${outputs.gsplat}.rgba.a = mix(color.a, 0.3, abs(fx.w) * intensity);
            }
          } else if (${inputs.family} == 3) {
            vec4 fx = dissolveEffect(localPos, t);
            ${outputs.gsplat}.center = localPos + fx.xyz * intensity;
            ${outputs.gsplat}.rgba = mix(color, vec4(1.0), clamp((fx.w - 1.0) * intensity, 0.0, 1.0));
            ${outputs.gsplat}.rgba.a = color.a * clamp(fx.w, 0.0, 1.0);
          } else {
            if (${inputs.effect} == 1) {
              vec4 fx = transitionSphere(localPos, scales, t, intensity);
              ${outputs.gsplat}.center = fx.xyz;
              ${outputs.gsplat}.scales = mix(vec3(0.004), scales, fx.w);
              ${outputs.gsplat}.rgba.a = color.a * fx.w;
            } else if (${inputs.effect} == 2) {
              vec4 fx = transitionExplosion(localPos, t, intensity);
              ${outputs.gsplat}.center = fx.xyz;
              ${outputs.gsplat}.scales = mix(vec3(0.004), scales, fx.w);
              ${outputs.gsplat}.rgba.a = color.a * fx.w;
            } else if (${inputs.effect} == 3) {
              vec4 fx = transitionFlow(localPos, t, intensity);
              ${outputs.gsplat}.center = fx.xyz;
              ${outputs.gsplat}.scales = mix(scales * 0.2, scales, fx.w);
              ${outputs.gsplat}.rgba.rgb *= 0.5 + fx.w * 0.5;
              ${outputs.gsplat}.rgba.a = color.a * clamp(fx.w, 0.0, 1.0);
            } else {
              vec4 fx = transitionMorph(localPos, ${inputs.gsplat}.index, t, 1.0 + intensity * 2.5);
              ${outputs.gsplat}.center = fx.xyz;
              ${outputs.gsplat}.scales = mix(scales * 0.2, scales, fx.w);
              ${outputs.gsplat}.rgba.a = color.a * fx.w;
            }
          }
        `)
      })

      gsplat = shader.apply({
        gsplat,
        time: animateT,
        enabled: effectEnabled,
        family: familyType,
        effect: effectType,
        intensity: effectIntensity,
        revealCenter,
        revealRadius
      }).gsplat

      return { gsplat }
    }
  )
}
